import { getFormStepErrors } from "@app/utils/inspection";
import getDisplayFields from "@app/lib/hooks/getDisplayFields";

export const updateField = (currentFieldsets, name, updateFn) => {
	return [
		...currentFieldsets.map((fieldset) => ({
			...fieldset,
			elements: fieldset.elements?.map((element) => {
				if (element.name === name) {
					return updateFn(element);
				}

				return element;
			}),
		})),
	];
};
export const findField = (currentFieldsets, fieldName) => {
	const fields = currentFieldsets.flatMap(({ elements }) => elements);
	return fields.find(({ name }) => name === fieldName);
};

export const findFieldset = (currentFieldsets, fieldName) => {
	return currentFieldsets.find(({ elements }) => elements.find(({ name }) => name === fieldName));
};

export const getInstantValidation = (values, fieldsets) => {
	const instantErrors = getFormStepErrors({ fieldsets }, values, true);
	return {
		isInstantValid: instantErrors?.length === 0,
		errors: instantErrors,
	};
};

export const isFieldValueEmpty = (value) => {
	if (Array.isArray(value)) return value.length === 0;
	if (value === null || value === undefined) return true;
	if (typeof value === "string") return value.trim() === "";
	return false;
};

// elementTypes from FormBuilderElements.js that are static layout/display — never a Formik field —
// but (like "subHeadline") can still carry a non-empty `name` in the config for observers to target.
// Left in the sweep they'd look like a permanently empty field and block the button forever.
const LAYOUT_ONLY_ELEMENT_TYPES = new Set([
	"headline",
	"subHeadline",
	"separator",
	"paragraph",
	"spacer",
	"accordion",
	"followUp",
	"definitionRow",
	"hint",
	"typicalRisksHint",
	"inspectionResultBox",
]);

// a "radioSwitcherList" (e.g. the injected "Standard Anforderungen" PSA/Vorsorgen/Unterweisungen
// lists, see StandardPsaObserver.utils.jsx) bundles several independent rows under one element:
// each row is its own Formik field at values[element.name][entry.id] (ListItem/FieldArrayHelper
// bind it via the bracket-path name `${element.name}['${entry.id}']`), so the element only counts
// as filled once every row has an answer
const isFieldFilled = (element, values) => {
	if (element.elementType === "radioSwitcherList") {
		const entryValues = values[element.name] || {};
		const entryIds = (element.entries || []).map(({ id }) => id);
		return entryIds.length === 0 || entryIds.every((id) => !isFieldValueEmpty(entryValues[id]));
	}
	return !isFieldValueEmpty(values[element.name]);
};

// splits the currently visible fields of a form step into unfilled *hard* Pflichtfelder
// (validation.instantCheck — these actually block navigation, see getInstantValidation) and any
// other unfilled visible field (Softpflicht via validation.required,
// which only shows the asterisk, or a fully optional field) — drives the Weiter button's
// orange/green + enabled/disabled state. Checked directly against the live values (not Formik's
// errors), since errors can lag a render behind a select's setFieldValue until something else
// forces a full revalidation.
// `fields` must be the live, observer-mutated field list (FormBuilder's `formikFields`), not the
// static step config — observers like LocationTypeAddressObserver/CustomerObserver/JobObserver set
// `disabled` (and other overrides) via an immutable copy (see updateField), so the original config
// objects never see it; reading the static config here would treat those fields as still enabled.
export const getVisibleFieldCompletion = (fields, values) => {
	const namedElements = (fields || [])
		// a disabled field (e.g. the customer/einsatzort address, auto-filled read-only once a
		// customer or address type is chosen) isn't something the user can act on here, so it
		// shouldn't keep the button from turning green
		.filter(
			({ name, disabled, elementType }) => !!name && !disabled && !LAYOUT_ONLY_ELEMENT_TYPES.has(elementType),
		);
	const visibleFields = getDisplayFields(namedElements, values);
	const unfilledRequired = visibleFields.filter(
		(field) => field.validation?.instantCheck && !isFieldFilled(field, values),
	);
	const unfilledOptional = visibleFields.filter(
		(field) => !field.validation?.instantCheck && !isFieldFilled(field, values),
	);

	return {
		hasUnfilledRequired: unfilledRequired.length > 0,
		hasUnfilledOptional: unfilledOptional.length > 0,
	};
};
